/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { clamp } from "./utils";
import { GrowableAudioBuffer } from "./buffer";


export interface WaveSample {
    wmin : number;
    wmax : number;
    wavg : number;
}

/**
 * Stores a compressed/reduced version of an audio buffer with just enough information to 
 * render a waveform visualization. 
 */
export class WaveformData {

    private waveform = new Array<WaveSample>();

    // source audio sample rate (hardcoded for now)
    readonly sampleRate = 44100;

    // webaudio works in process blocks of 128 frames
    readonly blockSize = 128;

    // how many waveform samples per pixel
    readonly stride = 8;

    // how many wave samples per second
    get waveRate() { return this.sampleRate / this.blockSize; }

    get isEmpty() { return this.waveform.length == 0; }

    // length of the waveform in seconds
    get duration() { return (this.waveform.length / this.waveRate); }

    // number of wave samples (segments)
    get samples() { return this.waveform.length; }


    private _top_path = new Array<string | number>();
    private _bottom_path = new Array<string | number>();


    /**
     * Create a new empty waveform
     */
    public constructor() { }


    /**
     * Appends one waveform sample representing 128 underlying audio frames
     */
    appendSample(sample : WaveSample) {
        function transform(x : number) {
            return Math.sin(x * Math.PI * 0.5);
        }
        this.waveform.push(sample);
        const time = this.duration;
        const y1 = clamp(transform(sample.wmax), 0, 1);
        //const y1 = Math.sign(sample.wmax) * clamp(Math.sqrt(Math.abs(sample.wmax)), 0, 1);
        const y2 = clamp(transform(sample.wmin), -1, 0);
        //const y2 = Math.sign(sample.wmin) * clamp(Math.sqrt(Math.abs(sample.wmin)), 0, 1);
        this._top_path.push("L", time, y1 + 0.01);
        this._bottom_path.unshift("L", time, y2 - 0.01);
    }


    /**
     * Append the given audio data to this waveform.
     */
    appendBuffer(buffer : AudioBuffer | GrowableAudioBuffer) {
        const left = buffer.getChannelData(0);
        const right = (buffer.numberOfChannels > 1) ? buffer.getChannelData(1) : left;
        const stride = this.blockSize * this.stride;
        let wmin = 0, wmax = 0, sum = 0;
        for (let i = 0; i < buffer.length; i++) {
            wmin = Math.min(wmin, Math.min(left[i], right[i]));
            wmax = Math.max(wmax, Math.max(left[i], right[i]));
            sum += (left[i] + right[i]) / 2;
            if (((i+1) % stride) === 0) {
                const wavg = sum / stride;
                this.appendSample({ wmin, wmax, wavg });
                wmax = 0.0;
                wmin = 0.0;
                sum = 0;
            }
        }
    }


    /**
     * Create a new buffer from the given AudioBuffer object
     */
    public static fromBuffer(buffer : AudioBuffer | GrowableAudioBuffer) : WaveformData {
        const wave = new WaveformData();
        wave.appendBuffer(buffer);
        return wave;
    }


    public get wavepath() : SVGPathElement {
        const path = document.createElementNS("http://www.w3.org/2000/svg", 'path');
        const d = [ "M", 0, 0, ...this._top_path, ...this._bottom_path, "Z" ].join(' ');
        path.setAttribute("d", d);
        return path;
    }
}
